import React from "react";
import { Box, Button, Typography, useTheme } from "@mui/material";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import AgentBaseCostBadge from "../../../common/AgentBaseCostBadge";
import AgentConfigSection from "../AgentConfigSection";
import { getModelCatalogEntry } from "../../../../utils/modelCatalog";
import { isSelfConfigurationEditEnabled } from "../selfConfiguration";
import {
  COST_SECTION_CARD_SX,
  CostBreakdownChart,
  buildOptimizationTips,
  buildOrderedCostItems,
} from "../costBreakdownUi";

function buildCostReductionMessage(instruction) {
  return [
    "I want to reduce your operating cost.",
    "",
    "First, call get_self_config to inspect your current configuration. Then apply ONLY the following change using update_self_config (send just the fields you are changing), and briefly explain what you changed and why it lowers cost:",
    "",
    instruction,
    "",
    "Do not modify any unrelated configuration.",
  ].join("\n");
}

export default function CostTab({
  agentBaseCost,
  model,
  selfConfigurationMode,
  agentId,
  onApplyTip,
}) {
  const theme = useTheme();
  const cost = agentBaseCost || { rawTokens: 0, tier: 1, modelCostTier: 1, breakdown: {} };
  const breakdown = cost.breakdown || {};
  const modelEntry = model ? getModelCatalogEntry(model) : null;
  const modelLabel = modelEntry?.label || model || "the selected model";
  const canApply =
    typeof onApplyTip === "function" &&
    Boolean(agentId) &&
    isSelfConfigurationEditEnabled(selfConfigurationMode);

  const itemsWithColors = buildOrderedCostItems(breakdown, theme);
  const tips = buildOptimizationTips(breakdown, cost.modelCostTier, modelLabel);

  return (
    <>
      <AgentConfigSection id="costSummary">
        <Box sx={COST_SECTION_CARD_SX}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography
                sx={{ fontSize: "13px", fontWeight: 600, color: "text.primary" }}
              >
                Base operating cost
              </Typography>
              <Typography
                sx={{ fontSize: "12px", color: "text.secondary", mt: 0.25 }}
              >
                Standing context sent on every turn, priced by {modelLabel}.
              </Typography>
            </Box>
            <AgentBaseCostBadge
              tier={cost.tier}
              rawTokens={cost.rawTokens}
              showLabel={false}
              sx={{ fontSize: "13px" }}
            />
          </Box>
          <Typography
            sx={{
              fontSize: "13px",
              color: "text.secondary",
              lineHeight: 1.6,
              mt: 1.5,
            }}
          >
            This is the cost the agent carries by how it is configured — its
            system prompt, tool definitions, knowledge, and scoped resources.
            It is charged on every turn before the agent does any work. It does
            not include the size of the user&apos;s request, the agent&apos;s output, or
            how many loop iterations a task takes — those vary per run.
          </Typography>
        </Box>
      </AgentConfigSection>

      <AgentConfigSection id="costBreakdown" sx={{ mt: 2.5 }}>
        <Typography className="org-field-label">Where the cost comes from</Typography>
        <Box sx={{ ...COST_SECTION_CARD_SX, mt: 1, pt: 1.5, pb: 1 }}>
          <CostBreakdownChart items={itemsWithColors} rawTokens={cost.rawTokens} />
        </Box>
      </AgentConfigSection>

      <AgentConfigSection id="costOptimization" sx={{ mt: 2.5 }}>
        <Typography className="org-field-label">How to reduce it</Typography>
        <Box sx={{ ...COST_SECTION_CARD_SX, mt: 1 }}>
          {tips.map((tip, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                py: 0.75,
              }}
            >
              <LightbulbOutlinedIcon
                sx={{ fontSize: 16, color: "text.secondary", mt: "1px", flexShrink: 0 }}
              />
              <Typography
                sx={{ fontSize: "13px", color: "text.secondary", lineHeight: 1.5, flex: 1 }}
              >
                {tip.text}
              </Typography>
              {canApply && tip.applicable ? (
                <Button
                  size="small"
                  variant="text"
                  startIcon={<AutoFixHighOutlinedIcon sx={{ fontSize: 15 }} />}
                  onClick={() =>
                    onApplyTip(buildCostReductionMessage(tip.instruction))
                  }
                  sx={{
                    textTransform: "none",
                    fontSize: "12px",
                    fontWeight: 500,
                    flexShrink: 0,
                    color: "primary.main",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                    py: 0,
                  }}
                >
                  Apply
                </Button>
              ) : null}
            </Box>
          ))}
          {typeof onApplyTip === "function" && !canApply ? (
            <Typography
              sx={{
                fontSize: "12px",
                color: "text.disabled",
                lineHeight: 1.5,
                mt: 1,
              }}
            >
              {agentId
                ? "Turn on “Allow agent to edit its own config” in the Advanced tab to apply these in one click."
                : "Save this agent first — then it can apply these changes from its chat."}
            </Typography>
          ) : null}
        </Box>
      </AgentConfigSection>
    </>
  );
}
